import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useNavigate, useParams } from "react-router-dom";
import { InvoiceApi, ProjectApi } from "../../../apis";

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });

function AddInvoice() {
  const { invoiceId } = useParams();
  const isEditing = Boolean(invoiceId);
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [phases, setPhases] = useState([]);
  const [notes, setNotes] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [status, setStatus] = useState("SENT");
  const [statusDate, setStatusDate] = useState("");
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingPhases, setLoadingPhases] = useState(false);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [loadingInvoiceNumber, setLoadingInvoiceNumber] = useState(false);
  const [saving, setSaving] = useState(false);
  const [projectSummary, setProjectSummary] = useState(null);
  const [isEditable, setIsEditable] = useState(!isEditing); // Locked by default if existing invoice
  const navigate = useNavigate();
  const invoiceRef = useRef(null);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        const res = await ProjectApi.getAllProjects({ status: "active" });
        const data = res?.data?.data?.projects || res?.data?.data || [];
        setProjects(data);
      } catch (error) {
        console.error("Error fetching projects:", error);
        toast.error("Unable to load projects");
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    if (!isEditing) return;
    const fetchInvoice = async () => {
      setLoadingInvoice(true);
      try {
        const res = await InvoiceApi.getInvoiceById(invoiceId);
        const invoice = res?.data?.data?.invoice || res?.data?.data || {};

        if (invoice.projectId || invoice.project?.id) {
          const projectId = invoice.projectId || invoice.project?.id;
          setSelectedProjectId(projectId.toString());
          setInvoiceDate(invoice.invoiceDate ? invoice.invoiceDate.split('T')[0] : "");
          setInvoiceNumber(invoice.invoiceNumber || "");
          setStatus(invoice.status || "SENT");
          setStatusDate(invoice.statusDate || "");
          setNotes(invoice.notes || "");

          // Load project phases and map invoice phases
          const projectRes = await InvoiceApi.getProjectData(projectId);
          const projectPayload = projectRes?.data?.data || {};
          const project = projectPayload.project || projectPayload.projectData || projectPayload || invoice.project || {};
          const projectPhases = Array.isArray(project.phases) ? project.phases : [];
          const invoicePhases = Array.isArray(invoice.phases) ? invoice.phases : [];

          // Create a map of invoice phases by phaseName for quick lookup
          const invoicePhaseMap = {};
          invoicePhases.forEach((invPhase) => {
            invoicePhaseMap[invPhase.phaseName] = invPhase;
          });

          const mapped = projectPhases.map((phase) => {
            const invPhase = invoicePhaseMap[phase.name || phase.phaseName];
            const contractAmount = Number(phase.contractAmount ?? phase.amount ?? 0);
            const previouslyBilledPercentRaw = invPhase?.previousPercent ?? phase.prevPercent ?? phase.previouslyBilledPercent ?? phase.percentComplete ?? 0;
            const previouslyBilledAmountRaw = invPhase?.billedAmount ?? phase.prevAmount ?? phase.previouslyBilledAmount ?? phase.billedAmount ?? 0;

            return {
              id: phase.id || phase.phaseId || phase.name || phase.phaseName,
              phaseId: phase.phaseId || phase.id,
              phaseName: phase.name || phase.phaseName || "Unnamed Phase",
              contractAmount,
              previouslyBilledPercent: previouslyBilledPercentRaw?.toString?.() ?? "",
              previouslyBilledAmount: previouslyBilledAmountRaw?.toString?.() ?? "",
              newPercentComplete: invPhase?.newPercent?.toString() ?? "",
              newBillAmount: invPhase?.billedAmount ?? 0,
              error: phase.errorMessage || phase.validationError || "",
            };
          });

          setPhases(mapped);
          setProjectSummary({
            name: project.name || project.projectName || "",
            projectName: project.projectName || project.name || "",
            client: project.client || null,
            totalContractAmount: Number(project.totalContractAmount ?? project.contractAmount ?? 0),
            totalInvoiceAmount: Number(invoice.totalAmount ?? invoice.totalInvoice ?? 0),
            invoiceDetails: project.invoiceDetails || "",
          });
        }
      } catch (error) {
        console.error("Error fetching invoice:", error);
        toast.error("Failed to load invoice");
      } finally {
        setLoadingInvoice(false);
      }
    };
    fetchInvoice();
  }, [isEditing, invoiceId]);

  useEffect(() => {
    // Skip if editing - invoice data is loaded in the edit useEffect
    if (isEditing) return;

    if (!selectedProjectId) {
      setPhases([]);
      setProjectSummary(null);
      return;
    }
    const fetchProjectPhases = async () => {
      setLoadingPhases(true);
      try {
        const res = await InvoiceApi.getProjectData(selectedProjectId);
        const payload = res?.data?.data || {};
        const project = payload.project || payload.projectData || payload || {};
        const projectPhases = Array.isArray(project.phases) ? project.phases : [];
        const mapped = projectPhases.map((phase) => {
          const contractAmount =
            Number(phase.contractAmount ?? phase.amount ?? 0);
          const previouslyBilledPercentRaw =
            phase.prevPercent ??
            phase.previouslyBilledPercent ??
            phase.percentComplete ??
            0;
          const previouslyBilledAmountRaw =
            phase.prevAmount ??
            phase.previouslyBilledAmount ??
            phase.billedAmount ??
            0;
          // Always start user input at blank; user will enter new % over time
          const defaultNewPercent = "";
          const defaultNewBill = 0;

          return {
            id: phase.id || phase.phaseId || phase.name || phase.phaseName,
            phaseId: phase.phaseId || phase.id,
            phaseName: phase.name || phase.phaseName || "Unnamed Phase",
            contractAmount,
            previouslyBilledPercent:
              previouslyBilledPercentRaw?.toString?.() ?? "",
            previouslyBilledAmount:
              previouslyBilledAmountRaw?.toString?.() ?? "",
            newPercentComplete: defaultNewPercent,
            newBillAmount: defaultNewBill,
            totalBilledToDate: Number(phase.totalBilledToDate ?? 0),
            error: phase.errorMessage || phase.validationError || "",
          };
        });
        setPhases(mapped);
        setProjectSummary({
          name: project.name || project.projectName || "",
          projectName: project.projectName || project.name || "",
          client: project.client || null,
          totalContractAmount: Number(
            project.totalContractAmount ?? project.contractAmount ?? 0
          ),
          totalInvoiceAmount: Number(project.totalInvoiceAmount ?? project.totalInvoice ?? 0),
          invoiceDetails: project.invoiceDetails || "",
        });
      } catch (error) {
        console.error("Error fetching phases:", error);
        toast.error("Unable to load project phases");
        setPhases([]);
        setProjectSummary(null);
      } finally {
        setLoadingPhases(false);
      }
    };
    fetchProjectPhases();
  }, [selectedProjectId, isEditing]);

  const handleFindNextInvoiceNumber = async () => {
    try {
      setLoadingInvoiceNumber(true);
      const res = await InvoiceApi.getNextInvoiceNumber();
      const nextNumber = res?.data?.data?.invoiceNumber || res?.data?.invoiceNumber || "";
      if (nextNumber) {
        setInvoiceNumber(nextNumber);
        toast.success(`Next invoice number: ${nextNumber}`);
      } else {
        toast.warning("Unable to determine next invoice number");
      }
    } catch (error) {
      console.error("Error fetching next invoice number:", error);
      toast.error("Failed to get next invoice number");
    } finally {
      setLoadingInvoiceNumber(false);
    }
  };

  const handlePrevPercentChange = (phaseId, value) => {
    setPhases((prev) =>
      prev.map((phase) => {
        if (phase.id !== phaseId) return phase;

        const numericValue = value === "" ? NaN : Number(value);
        let errorMessage = "";

        if (value !== "" && Number.isNaN(numericValue)) {
          errorMessage = "Invalid number";
        } else if (numericValue < 0) {
          errorMessage = "Percent cannot be negative";
        } else if (numericValue > 100) {
          errorMessage = "Percent cannot exceed 100";
        } else if (
          !isEditing &&
          phase.newPercentComplete !== "" &&
          numericValue > Number(phase.newPercentComplete)
        ) {
          errorMessage =
            "Previous percent cannot be greater than New Percent Complete";
        }

        const updatedPrevPercent =
          value === "" || Number.isNaN(numericValue) ? "" : numericValue;

        // Recalculate previous amount from updated prev % if we have a number
        const updatedPrevAmount =
          value === "" || Number.isNaN(numericValue)
            ? ""
            : (numericValue / 100) * phase.contractAmount;

        // Recalculate new bill amount based on current new % and updated prev %
        let newBillAmount = phase.newBillAmount;
        if (
          phase.newPercentComplete !== "" &&
          !Number.isNaN(Number(phase.newPercentComplete)) &&
          !Number.isNaN(numericValue) &&
          Number(phase.newPercentComplete) >= numericValue &&
          numericValue >= 0 &&
          numericValue <= 100
        ) {
          const percentDelta =
            (Number(phase.newPercentComplete) - updatedPrevPercent) / 100;
          newBillAmount = percentDelta * phase.contractAmount;
          if (!isEditing && newBillAmount < 0) {
            newBillAmount = 0;
          }
        } else if (errorMessage) {
          newBillAmount = 0;
        }

        // Ensure previous + new do not exceed contract amount
        const prevAmtNum =
          updatedPrevAmount === "" ? 0 : Number(updatedPrevAmount);
        if (
          !Number.isNaN(prevAmtNum) &&
          !Number.isNaN(Number(newBillAmount)) &&
          prevAmtNum + Number(newBillAmount) > phase.contractAmount
        ) {
          errorMessage =
            "Total billed (previous + new) cannot exceed contract amount";
          newBillAmount = Math.max(
            0,
            phase.contractAmount - prevAmtNum
          );
        }

        return {
          ...phase,
          previouslyBilledPercent: updatedPrevPercent,
          previouslyBilledAmount: updatedPrevAmount,
          newBillAmount,
          error: errorMessage,
        };
      })
    );
  };

  const handlePrevAmountChange = (phaseId, value) => {
    setPhases((prev) =>
      prev.map((phase) => {
        if (phase.id !== phaseId) return phase;

        const numericValue = value === "" ? NaN : Number(value);
        let errorMessage = "";

        if (value !== "" && Number.isNaN(numericValue)) {
          errorMessage = "Invalid number";
        } else if (numericValue < 0) {
          errorMessage = "Amount cannot be negative";
        }

        const updatedPrevAmount =
          value === "" || Number.isNaN(numericValue) ? "" : numericValue;

        // Derive previous percent from amount if possible
        let updatedPrevPercent = phase.previouslyBilledPercent;
        if (!Number.isNaN(numericValue) && phase.contractAmount > 0) {
          updatedPrevPercent = (numericValue / phase.contractAmount) * 100;
          if (updatedPrevPercent > 100) {
            errorMessage = "Previous percent cannot exceed 100";
          }
        } else if (value === "") {
          updatedPrevPercent = "";
        }

        // Recalculate new bill amount using current new % and updated prev %
        let newBillAmount = phase.newBillAmount;
        if (
          phase.newPercentComplete !== "" &&
          !Number.isNaN(Number(phase.newPercentComplete)) &&
          !Number.isNaN(Number(updatedPrevPercent)) &&
          Number(phase.newPercentComplete) >= Number(updatedPrevPercent) &&
          Number(updatedPrevPercent) >= 0 &&
          Number(updatedPrevPercent) <= 100
        ) {
          const percentDelta =
            (Number(phase.newPercentComplete) - Number(updatedPrevPercent)) /
            100;
          newBillAmount = percentDelta * phase.contractAmount;
          if (!isEditing && newBillAmount < 0) {
            newBillAmount = 0;
          }
        } else if (errorMessage) {
          newBillAmount = 0;
        }

        // Ensure previous + new do not exceed contract amount
        const prevAmtNum =
          updatedPrevAmount === "" ? 0 : Number(updatedPrevAmount);
        if (
          !Number.isNaN(prevAmtNum) &&
          !Number.isNaN(Number(newBillAmount)) &&
          prevAmtNum + Number(newBillAmount) > phase.contractAmount
        ) {
          errorMessage =
            "Total billed (previous + new) cannot exceed contract amount";
          newBillAmount = Math.max(
            0,
            phase.contractAmount - prevAmtNum
          );
        }

        return {
          ...phase,
          previouslyBilledPercent: updatedPrevPercent,
          previouslyBilledAmount: updatedPrevAmount,
          newBillAmount,
          error: errorMessage,
        };
      })
    );
  };

  const handleNewBillAmountChange = (phaseId, value) => {
    setPhases((prev) =>
      prev.map((phase) => {
        if (phase.id !== phaseId) return phase;

        const numericValue = value === "" ? NaN : Number(value);
        let errorMessage = "";
        let newPercentComplete = phase.newPercentComplete;

        if (value !== "" && Number.isNaN(numericValue)) {
          errorMessage = "Invalid number";
        } else if (numericValue < 0) {
          errorMessage = "Amount cannot be negative";
        } else if (phase.contractAmount <= 0) {
          errorMessage = "Contract amount must be greater than 0";
        } else {
          // Derive new percent from previous percent and new bill amount
          const deltaPercent =
            (numericValue / phase.contractAmount) * 100;
          const basePrevPercent = Number(
            phase.previouslyBilledPercent || 0
          );
          newPercentComplete = basePrevPercent + deltaPercent;

          if (!isEditing && newPercentComplete < basePrevPercent) {
            errorMessage =
              "New Percent Complete Cannot Be Less Than Previously Billed!";
          } else if (newPercentComplete > 100) {
            errorMessage = "Percent cannot exceed 100";
          }
        }

        if (errorMessage) {
          return {
            ...phase,
            newPercentComplete:
              value === "" ? "" : newPercentComplete || "",
            newBillAmount: value === "" ? "" : numericValue,
            error: errorMessage,
          };
        }

        return {
          ...phase,
          newPercentComplete:
            value === ""
              ? ""
              : Number(newPercentComplete.toFixed(2)),
          newBillAmount: value === "" ? "" : numericValue,
          error: "",
        };
      })
    );
  };

  const handlePercentChange = (phaseId, value) => {
    setPhases((prev) =>
      prev.map((phase) => {
        if (phase.id !== phaseId) return phase;

        const numericValue = value === "" ? NaN : Number(value);
        const prevPercent = Number(phase.previouslyBilledPercent || 0);
        const contractAmount = Number(phase.contractAmount || 0);
        const prevAmount = Number(phase.previouslyBilledAmount || 0);

        let errorMessage = "";
        let newBillAmount = 0;

        if (value !== "" && Number.isNaN(numericValue)) {
          errorMessage = "Invalid number";
        } else if (!isEditing && numericValue < prevPercent) {
          errorMessage =
            "New Percent Complete Cannot Be Less Than Previously Billed!";
        } else if (numericValue > 100) {
          errorMessage = "Percent cannot exceed 100";
        } else if (contractAmount <= 0) {
          errorMessage = "Contract amount must be greater than 0";
        } else {
          const percentDelta = (numericValue - prevPercent) / 100;
          newBillAmount = percentDelta * contractAmount;
          if (!isEditing && newBillAmount < 0) {
            newBillAmount = 0;
          }

          // Ensure previous + new do not exceed contract amount
          if (prevAmount + newBillAmount > contractAmount) {
            errorMessage =
              "Total billed (previous + new) cannot exceed contract amount";
            newBillAmount = Math.max(0, contractAmount - prevAmount);
          }
        }

        return {
          ...phase,
          newPercentComplete: value === "" ? "" : numericValue,
          newBillAmount,
          error: errorMessage,
        };
      })
    );
  };

  const totalPrevAmount = useMemo(
    () =>
      phases.reduce(
        (sum, phase) => sum + Number(phase.previouslyBilledAmount || 0),
        0
      ),
    [phases]
  );

  const totalBillAmount = useMemo(() => {
    return phases.reduce(
      (sum, phase) => sum + Number(phase.newBillAmount || 0),
      0
    );
  }, [phases]);

  const totalContractAmount = useMemo(() => {
    const backendTotal =
      projectSummary && projectSummary.totalContractAmount
        ? Number(projectSummary.totalContractAmount)
        : 0;
    const sumFromPhases = phases.reduce(
      (sum, phase) => sum + Number(phase.contractAmount || 0),
      0
    );
    // Prefer backend total when it's > 0, otherwise fall back to phase sum
    return backendTotal > 0 ? backendTotal : sumFromPhases;
  }, [projectSummary, phases]);

  const hasErrors = useMemo(() => {
    if (!selectedProjectId) return true;
    if (!invoiceNumber || !invoiceNumber.trim()) return true;

    const hasPhaseErrors = phases.some((phase) => phase.error);
    if (hasPhaseErrors) return true;

    // When editing, allow saving even if no new bills (preserving existing invoice)
    // When creating, require at least one line item with a positive new bill amount
    if (!isEditing) {
      const hasAnyBill = phases.some(
        (phase) => Number(phase.newBillAmount || 0) > 0
      );
      if (!hasAnyBill) return true;
    }

    // Do not allow total billed (prev + new) to exceed total contract
    if (
      totalContractAmount > 0 &&
      totalPrevAmount + totalBillAmount > totalContractAmount
    ) {
      return true;
    }

    return false;
  }, [selectedProjectId, invoiceNumber, phases, totalPrevAmount, totalBillAmount, totalContractAmount, isEditing]);

  const handleSaveInvoice = async () => {
    if (!selectedProjectId) {
      toast.error("Please select a project");
      return;
    }
    if (!invoiceDate) {
      toast.error("Invoice date is required");
      return;
    }
    if (!invoiceNumber || !invoiceNumber.trim()) {
      toast.error("Invoice number is required");
      return;
    }
    if (phases.length === 0) {
      toast.error("No phases available for this project");
      return;
    }
    // Only check for validation errors when creating new invoices
    if (!isEditing && hasErrors) {
      toast.error("Resolve validation errors before saving");
      return;
    }
    // When creating, only include phases with positive bill amounts
    // When editing, include all phases that have data (to preserve the invoice structure)
    const phasesPayload = phases
      .filter((phase) => {
        if (isEditing) {
          // When editing, include phases that have a newPercentComplete value
          return phase.newPercentComplete !== "" && phase.newPercentComplete !== null && phase.newPercentComplete !== undefined;
        } else {
          // When creating, only include phases with positive bill amounts
          return Number(phase.newBillAmount || 0) > 0;
        }
      })
      .map((phase) => ({
        phaseName: phase.phaseName,
        contractAmount: Number(phase.contractAmount || 0),
        previousPercent: Number(phase.previouslyBilledPercent || 0),
        newPercent: Number(phase.newPercentComplete),
        billedAmount: Number(phase.newBillAmount),
      }));

    if (!phasesPayload.length) {
      toast.error(isEditing ? "Invoice must have at least one phase" : "At least one phase is required");
      return;
    }

    const payload = {
      projectId: selectedProjectId,
      invoiceNumber: invoiceNumber.trim(),
      invoiceDate,
      notes,
      status,
      statusDate,
      phases: phasesPayload,
      totalAmount: totalBillAmount,
    };
    try {
      setSaving(true);
      if (isEditing) {
        await InvoiceApi.updateInvoice(invoiceId, payload);
        toast.success("Invoice updated successfully");
      } else {
        await InvoiceApi.createInvoice(payload);
        toast.success("Invoice saved successfully");
      }
      navigate("/admin/invoices");
    } catch (error) {
      console.error("Error saving invoice:", error);
      toast.error(error.response?.data?.message || "Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  const handleExportPDF = async () => {
    if (!phases.length || !selectedProjectId) {
      toast.error("No invoice data to export");
      return;
    }

    try {
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 30;
      const colGap = 10;
      const colWidth = (pageWidth - 2 * margin - colGap) / 2;
      let yPos = margin;
      const lineHeight = 3.5;
      const sectionSpacing = 2;

      pdf.setTextColor(0, 0, 0);

      // Load and add logo image
      const logoImg = new Image();
      logoImg.src = "/images/invoice_logo.png";

      let logoDisplayHeight = 0;

      await new Promise((resolve, reject) => {
        logoImg.onload = () => {
          // Add logo image (maintain aspect ratio)
          const logoWidth = 65; // mm
          const aspectRatio = logoImg.height / logoImg.width;
          logoDisplayHeight = logoWidth * aspectRatio;
          pdf.addImage(logoImg, "PNG", margin, yPos, logoWidth, logoDisplayHeight);
          resolve();
        };
        logoImg.onerror = () => {
          // Fallback to text if image fails to load
          pdf.setFontSize(16);
          pdf.setFont("helvetica", "bold");
          pdf.text("GEOPRO", margin, yPos + 5);
          pdf.setFontSize(7);
          pdf.setFont("helvetica", "normal");
          pdf.setCharSpace(0.5);
          pdf.text("C O N S U L T A N T S", margin, yPos + 10);
          pdf.setCharSpace(0);
          logoDisplayHeight = 15; // Approx height of text
          resolve();
        };
      });

      // INVOICE title on right (light gray)
      pdf.setFontSize(30);
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(220, 220, 220);
      pdf.text("INVOICE", pageWidth - margin, yPos + 11, { align: "right" });
      pdf.setTextColor(0, 0, 0);

      // Adjust yPos based on logo height
      yPos += Math.max(logoDisplayHeight, 25) + 10;

      // Two-column layout for sender info and invoice details
      const leftColX = margin;
      const rightColX = margin + colWidth + colGap;
      let leftYPos = yPos;
      let rightYPos = yPos;

      // Left Column: Mail payment info
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "bold");
      pdf.text("Please mail payment to:", leftColX, leftYPos);
      leftYPos += lineHeight;
      pdf.setFont("helvetica", "normal");
      pdf.text("Geopro Consultants, LLC", leftColX, leftYPos);
      leftYPos += lineHeight;
      pdf.text("1441 East Stroop Road", leftColX, leftYPos);
      leftYPos += lineHeight;
      pdf.text("Dayton, OH 45429", leftColX, leftYPos);
      leftYPos += lineHeight;
      pdf.text("Phone 937.674.1492", leftColX, leftYPos);
      leftYPos += sectionSpacing * 2;

      // Right Column: Date and Invoice Number
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "bold");
      // Align label to the right
      pdf.text("Date:", rightColX + 28, rightYPos, { align: "right" });
      pdf.setFont("helvetica", "normal");
      const formattedDate = invoiceDate
        ? new Date(invoiceDate).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
        : new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
      // Align value to the left
      pdf.text(formattedDate, rightColX + 30, rightYPos, { align: "left" });

      rightYPos += lineHeight;
      pdf.setFont("helvetica", "bold");
      // Align label to the right
      pdf.text("Invoice Number:", rightColX + 28, rightYPos, { align: "right" });
      pdf.setFont("helvetica", "normal");
      // Align value to the left
      pdf.text(invoiceNumber || "N/A", rightColX + 30, rightYPos, { align: "left" });
      rightYPos += sectionSpacing * 2;

      // Update yPos to max of both columns
      yPos = Math.max(leftYPos, rightYPos);

      // Add extra spacing before Bill To section
      yPos += 5;

      // Two-column layout for Bill To and Project Details
      leftYPos = yPos;
      rightYPos = yPos;

      // Bill To Section (Left Column) - Order: Company Name, Contact Person, Billing Address, City State Zip, Accounts Payable Email
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "bold");
      pdf.text("Bill To:", leftColX, leftYPos);
      leftYPos += lineHeight;

      pdf.setFontSize(7);
      pdf.setFont("helvetica", "normal");
      if (projectSummary?.client) {
        const client = projectSummary.client;

        // Company Name
        if (client.company || client.clientNickname) {
          pdf.text(client.company || client.clientNickname, leftColX, leftYPos);
          leftYPos += lineHeight;
        }
        // Contact Person
        if (client.contactPerson) {
          pdf.text(client.contactPerson, leftColX, leftYPos);
          leftYPos += lineHeight;
        }
        // Billing Address
        if (client.billingAddress) {
          pdf.text(client.billingAddress, leftColX, leftYPos);
          leftYPos += lineHeight;
        }
        // Billing City, State, Zip Code
        const cityStateZip = [
          client.billingCity || client.city,
          client.billingState || client.state,
          client.billingZipCode || client.zipCode
        ]
          .filter(Boolean)
          .join(", ");
        if (cityStateZip) {
          pdf.text(cityStateZip, leftColX, leftYPos);
          leftYPos += lineHeight;
        }
        // Accounts Payable Email
        if (client.accountsPayableEmail) {
          pdf.text(client.accountsPayableEmail, leftColX, leftYPos);
          leftYPos += lineHeight;
        }
      }

      // Project Tasks Section (Right Column)
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "bold");
      pdf.text("Project Tasks", rightColX, rightYPos);
      // Removed margin top: rightYPos += lineHeight; 
      // Add a tiny offset so box doesn't overlap text descenders
      const boxTopY = rightYPos + 1;

      // Draw box for Project Details
      const projectDetails = projectSummary?.invoiceDetails || "";
      const boxWidth = colWidth; // Adjust width to fit column
      const padding = 2;

      pdf.setFontSize(7);
      pdf.setFont("helvetica", "normal");

      // Calculate text lines and height
      const detailsLines = pdf.splitTextToSize(projectDetails, boxWidth - 2 * padding);
      // Minimum height for the box to look good, or dynamic based on text
      const minBoxHeight = 20;
      const contentHeight = detailsLines.length * lineHeight + 2 * padding;
      const boxHeight = Math.max(minBoxHeight, contentHeight);

      // Draw the box
      pdf.setFillColor(242, 242, 242); // Light grey background
      pdf.setDrawColor(0, 0, 0); // Black border
      pdf.rect(rightColX, boxTopY, boxWidth, boxHeight, "FD");

      // Add text inside the box
      if (projectDetails) {
        pdf.text(detailsLines, rightColX + padding, boxTopY + lineHeight);
      }

      // Update rightYPos for subsequent elements
      rightYPos = boxTopY + boxHeight;



      yPos = Math.max(leftYPos, rightYPos) + sectionSpacing * 2;

      // Table setup
      const rowHeight = 4.5;
      const col1X = margin;
      const col1Width = 66;
      const col2X = col1X + col1Width;
      const col2Width = 28;
      const col3X = col2X + col2Width;
      const col3Width = 28;
      const col4X = col3X + col3Width;
      const col4Width = 28;
      const tableWidth = col1Width + col2Width + col3Width + col4Width;

      // Table Header Row
      // Table Header Row
      const headerRowHeight = 8;

      pdf.setFillColor(220, 220, 220);
      pdf.setDrawColor(0, 0, 0);

      // Draw individual bordered rectangles for each header cell
      pdf.rect(col1X, yPos - 4, col1Width, headerRowHeight, "FD");
      pdf.rect(col2X, yPos - 4, col2Width, headerRowHeight, "FD");
      pdf.rect(col3X, yPos - 4, col3Width, headerRowHeight, "FD");
      pdf.rect(col4X, yPos - 4, col4Width, headerRowHeight, "FD");

      pdf.setFontSize(7);
      pdf.setFont("helvetica", "bold");

      // Calculate vertical center offset
      // yPos is currently the baseline for text if we didn't change it.
      // The rect starts at yPos - 4.
      // We want text to be centered in the rect.
      // Rect top: yPos - 4
      // Rect height: 10
      // Rect bottom: yPos - 4 + 10 = yPos + 6
      // Center Y: yPos - 4 + 5 = yPos + 1
      const textY = yPos + 1;

      // Project Tasks (Left Aligned)
      pdf.text("Project Tasks", col1X + 2, textY);

      // Amount (Centered)
      pdf.text("Amount", col2X + col2Width / 2, textY, { align: "center" });

      // Percent Complete (Centered)
      pdf.text("Percent", col3X + col3Width / 2, textY - 1.5, { align: "center" });
      pdf.text("Complete", col3X + col3Width / 2, textY + 1.5, { align: "center" });

      // Total (Centered)
      pdf.text("Total", col4X + col4Width / 2, textY, { align: "center" });

      yPos += headerRowHeight;

      // Table Data Rows
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "normal");
      // Show all phases in the PDF
      const phasesToDisplay = phases.length > 0 ? phases : [];

      pdf.setDrawColor(0, 0, 0);
      if (phasesToDisplay.length === 0) {
        // Draw empty row border
        pdf.line(col1X, yPos - 4, col1X + tableWidth, yPos - 4);
        pdf.line(col1X, yPos - 4, col1X, yPos + rowHeight - 4);
        pdf.line(col2X, yPos - 4, col2X, yPos + rowHeight - 4);
        pdf.line(col3X, yPos - 4, col3X, yPos + rowHeight - 4);
        pdf.line(col4X, yPos - 4, col4Width, yPos + rowHeight - 4); // Fix col4 line
        pdf.line(col1X + tableWidth, yPos - 4, col1X + tableWidth, yPos + rowHeight - 4);
        pdf.text("No phases available", col1X + 2, yPos);
        yPos += rowHeight;
      } else {
        phasesToDisplay.forEach((phase) => {
          // Draw row borders
          pdf.line(col1X, yPos - 4, col1X + tableWidth, yPos - 4);
          pdf.line(col1X, yPos - 4, col1X, yPos + rowHeight - 4);
          pdf.line(col2X, yPos - 4, col2X, yPos + rowHeight - 4);
          pdf.line(col3X, yPos - 4, col3X, yPos + rowHeight - 4);
          pdf.line(col4X, yPos - 4, col4X, yPos + rowHeight - 4);
          pdf.line(col1X + tableWidth, yPos - 4, col1X + tableWidth, yPos + rowHeight - 4);

          const phaseName = phase.phaseName.length > 28
            ? phase.phaseName.substring(0, 25) + "..."
            : phase.phaseName;
          pdf.text(phaseName, col1X + 2, yPos);

          // Center Amount
          pdf.text(formatCurrency(phase.contractAmount), col2X + col2Width / 2, yPos, { align: "center" });

          // Use newPercentComplete if available, otherwise use previouslyBilledPercent
          const percentComplete = phase.newPercentComplete !== "" && phase.newPercentComplete !== null && phase.newPercentComplete !== undefined
            ? Number(phase.newPercentComplete)
            : (phase.previouslyBilledPercent !== "" && phase.previouslyBilledPercent !== null && phase.previouslyBilledPercent !== undefined
              ? Number(phase.previouslyBilledPercent)
              : 0);

          // Center Percent Complete
          pdf.text(`${percentComplete}%`, col3X + col3Width / 2, yPos, { align: "center" });

          // Center Total
          pdf.text(formatCurrency(phase.newBillAmount || 0), col4X + col4Width / 2, yPos, { align: "center" });

          yPos += rowHeight;
        });
      }

      // Close main table
      // Close main table (only for first column since others continue)
      pdf.line(col1X, yPos - 4, col2X, yPos - 4);

      // Summary Section (aligned with last three columns)
      const summaryRowHeight = 5;

      // Helper to draw summary row
      const drawSummaryRow = (label, value, isLast = false) => {
        // Draw borders for Amount Cell (Col 2) - Empty but bordered
        if (isLast) {
          pdf.setFillColor(220, 220, 220);

          // Only Col 4 (Value) has grey background and borders
          pdf.setFillColor(220, 220, 220);
          pdf.rect(col4X, yPos - 4, col4Width, summaryRowHeight, "FD");

          // Col 2 and Col 3: Explicitly white background, no borders
          // Offset slightly to avoid overwriting the top border (from previous row)
          pdf.setFillColor(255, 255, 255);
          pdf.rect(col2X, yPos - 4 + 0.1, col2Width + col3Width, summaryRowHeight - 0.1, "F");

        } else {
          // Merge Col 2 and Col 3 to remove the border between them
          pdf.rect(col2X, yPos - 4, col2Width + col3Width, summaryRowHeight);
          pdf.rect(col4X, yPos - 4, col4Width, summaryRowHeight);
        }

        // Label (Right aligned in Col 3)
        pdf.setFont("helvetica", "bold");
        pdf.text(label, col3X + col3Width - 2, yPos, { align: "right" });

        // Value (Right aligned in Col 4)
        pdf.setFont("helvetica", "normal");
        pdf.text(value, col4X + col4Width - 2, yPos, { align: "right" });

        yPos += summaryRowHeight;
      };

      drawSummaryRow("Sub-Total:", formatCurrency(totalBillAmount));
      drawSummaryRow("Previously Invoiced:", formatCurrency(totalPrevAmount));
      drawSummaryRow("Total Due:", formatCurrency(totalBillAmount), true);

      // Reduced spacing above Project Status Notes
      yPos += sectionSpacing;

      // Project Status Notes (Invoice Notes) - in a box
      if (notes && notes.trim()) {
        const boxWidth = pageWidth - 2 * margin;
        const headerHeight = 6;
        const padding = 4;

        // Calculate content height
        pdf.setFontSize(8);
        pdf.setFont("helvetica", "normal");
        const notesLines = pdf.splitTextToSize(notes, boxWidth - 2 * padding);
        const contentHeight = notesLines.length * lineHeight + 2 * padding;
        const totalBoxHeight = headerHeight + contentHeight;

        // Draw gray header box
        pdf.setFillColor(220, 220, 220);
        pdf.rect(margin, yPos, boxWidth, headerHeight, "FD");

        // Draw white content box
        pdf.setFillColor(255, 255, 255);
        pdf.rect(margin, yPos + headerHeight, boxWidth, contentHeight, "FD");

        // Draw border around entire box
        pdf.setDrawColor(0, 0, 0);
        pdf.rect(margin, yPos, boxWidth, totalBoxHeight, "D");

        // Header text
        pdf.setFontSize(7);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(0, 0, 0);
        pdf.text("Projects Status Notes:", margin + 1, yPos + 3);

        // Content text
        pdf.setFontSize(7);
        pdf.setFont("helvetica", "normal");
        pdf.text(notesLines, margin + padding, yPos + headerHeight + padding + 4);

        yPos += totalBoxHeight + sectionSpacing;
      }

      // Footer
      const footerY = yPos + 1;
      pdf.setFontSize(7);
      pdf.setFont("helvetica", "normal");
      pdf.text("Make all checks payable to Geopro Consultants, LLC", margin, footerY);
      pdf.setFont("helvetica", "normal");
      pdf.text("If you have any questions concerning this invoice, contact me.", margin, footerY + lineHeight);
      pdf.setFont("helvetica", "bold");
      pdf.text("THANK YOU FOR YOUR BUSINESS!", pageWidth / 2, footerY + lineHeight * 3, { align: "center" });

      // Generate filename
      const filename = invoiceNumber
        ? `Invoice_${invoiceNumber.replace(/\s+/g, "_")}.pdf`
        : `Invoice_Draft_${new Date().toISOString().split("T")[0]}.pdf`;

      pdf.save(filename);
      toast.success("PDF exported successfully");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF");
    }
  };

  const handleExportExcel = async () => {
    if (!phases.length) {
      toast.error("No phases to export");
      return;
    }

    try {
      // Create workbook and worksheet
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Invoice", {
        views: [{ showGridLines: false }],
        pageSetup: { paperSize: 1, orientation: "portrait", margins: { left: 0.7, right: 0.7, top: 0.75, bottom: 0.75, header: 0.3, footer: 0.3 } }
      });

      // Define Columns
      worksheet.columns = [
        { header: "", key: "A", width: 45 }, // Project Tasks / Info
        { header: "", key: "B", width: 15 }, // Amount
        { header: "", key: "C", width: 25 }, // Percent / Project Details Label
        { header: "", key: "D", width: 25 }, // Total / Project Details Box
        { header: "", key: "E", width: 15 }, // Extra
      ];

      // --- 1. Logo ---
      // Fetch logo image
      const logoPath = "/images/invoice_logo.png";
      const logoResponse = await fetch(logoPath);
      const logoBlob = await logoResponse.blob();
      const logoBuffer = await logoBlob.arrayBuffer();

      const logoId = workbook.addImage({
        buffer: logoBuffer,
        extension: "png",
      });

      // Get image dimensions to use 100% scale
      const img = new Image();
      img.src = logoPath;
      await new Promise((resolve) => {
        img.onload = resolve;
        img.onerror = resolve; // Continue even if load fails
      });

      // Use natural dimensions if available, otherwise fallback
      const imgWidth = img.naturalWidth || 360;
      const imgHeight = img.naturalHeight || 99;

      // Add image to worksheet (A1:B3 approx)
      worksheet.addImage(logoId, {
        tl: { col: 0, row: 0 },
        ext: { width: imgWidth, height: imgHeight },
      });

      // --- 2. Header Info ---
      // INVOICE Title (C1:D2)
      const invoiceTitleCell = worksheet.getCell("C1");
      invoiceTitleCell.value = "INVOICE";
      invoiceTitleCell.font = { name: "Helvetica", size: 28, bold: true, color: { argb: "FF808080" } }; // Darker Grey
      invoiceTitleCell.alignment = { horizontal: "right", vertical: "middle" };
      worksheet.mergeCells("C1:D2");

      // Spacing rows for logo
      // Calculate required spacing based on image height (approx 20px per row)
      const rowHeightPx = 20;
      const requiredRows = Math.ceil(imgHeight / rowHeightPx);
      // Ensure at least a few buffer rows, but add more if logo is tall
      const bufferRows = Math.max(3, requiredRows - 2); // -2 accounting for rows 1 & 2 used by title

      for (let i = 0; i < bufferRows; i++) {
        worksheet.addRow([]);
      }
      worksheet.addRow([]); // Extra spacing row for readability

      // --- 3. Company & Invoice Info ---
      // Row 4
      const r4 = worksheet.addRow(["Please mail payment to:", "", "Date:", "", ""]);
      r4.getCell(1).font = { name: "Helvetica", size: 10, bold: true };
      r4.getCell(3).font = { name: "Helvetica", size: 10, bold: true };
      r4.getCell(3).alignment = { horizontal: "right" };

      const formattedDate = invoiceDate
        ? new Date(invoiceDate).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
        : new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });
      r4.getCell(4).value = formattedDate;
      r4.getCell(4).font = { name: "Helvetica", size: 10 };
      r4.getCell(4).alignment = { horizontal: "left" };

      // Row 5
      const r5 = worksheet.addRow(["Geopro Consultants, LLC", "", "Invoice Number:", "", ""]);
      r5.getCell(1).font = { name: "Helvetica", size: 10 };
      r5.getCell(3).font = { name: "Helvetica", size: 10, bold: true };
      r5.getCell(3).alignment = { horizontal: "right" };
      r5.getCell(4).value = invoiceNumber || "N/A";
      r5.getCell(4).font = { name: "Helvetica", size: 10 };
      r5.getCell(4).alignment = { horizontal: "left" };

      // Address Rows
      const addrRows = [
        ["1441 East Stroop Road"],
        ["Dayton, OH 45429"],
        ["Phone 937.674.1492"]
      ];
      addrRows.forEach(row => {
        const r = worksheet.addRow(row);
        r.getCell(1).font = { name: "Helvetica", size: 10 };
      });

      worksheet.addRow([]); // Spacing

      // --- 4. Bill To & Project Tasks ---  Order: Company Name, Contact Person, Billing Address, City State Zip, Accounts Payable Email
      const r11 = worksheet.addRow(["Bill To:", "", "Required Client Details:", "", ""]);
      r11.getCell(1).font = { name: "Helvetica", size: 10, bold: true };
      r11.getCell(3).font = { name: "Helvetica", size: 10, bold: true };

      // Client Info (Bill To structure)
      const client = projectSummary?.client || {};
      const clientInfo = [
        client.company || client.clientNickname || "",
        client.contactPerson || "",
        client.billingAddress || "",
        [
          client.billingCity || client.city,
          client.billingState || client.state,
          client.billingZipCode || client.zipCode
        ].filter(Boolean).join(", "),
        client.accountsPayableEmail || ""
      ].filter(Boolean);

      // Project Tasks content
      const projectDetails = projectSummary?.invoiceDetails || "";

      // Add rows
      const startRow = 12;
      const maxRows = Math.max(clientInfo.length, 6); // Ensure at least some height for the box

      for (let i = 0; i < maxRows; i++) {
        const cInfo = clientInfo[i] || "";
        const row = worksheet.addRow([cInfo, "", "", "", ""]);
        row.getCell(1).font = { name: "Helvetica", size: 10 };
      }

      // Merge and Style Project Details Box
      // Merge C12 to D(12+maxRows-1)
      const endRow = startRow + maxRows - 1;
      worksheet.mergeCells(`C${startRow}:D${endRow}`);
      const pdCell = worksheet.getCell(`C${startRow}`);
      pdCell.value = projectDetails;
      pdCell.font = { name: "Helvetica", size: 10 };
      pdCell.alignment = { vertical: "top", wrapText: true };
      pdCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } }; // Light Grey
      pdCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
      };

      worksheet.addRow([]); // Spacing

      // --- 5. Table ---
      // Header
      const headerRow = worksheet.addRow(["Project Tasks", "Amount", "Percent Complete", "Total"]);
      headerRow.height = 30; // Increase height
      ["A", "B", "C", "D"].forEach((col, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.font = { name: "Helvetica", size: 10, bold: true };
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
        cell.alignment = { vertical: "middle", horizontal: index === 0 ? "left" : "center", wrapText: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" }
        };
      });
      // Merge D and E for Total? No, E is extra. Let's merge D and E for the table width?
      // The PDF has 4 columns. Excel has 5 columns defined (A, B, C, D, E).
      // Let's make the table span A, B, C, D. E is unused/margin?
      // Actually, let's stick to 4 columns for the table.

      // Data
      const firstDataRow = headerRow.number + 1;
      phases.forEach((phase, index) => {
        const percentComplete = phase.newPercentComplete !== "" && phase.newPercentComplete !== null && phase.newPercentComplete !== undefined
          ? Number(phase.newPercentComplete)
          : (phase.previouslyBilledPercent !== "" && phase.previouslyBilledPercent !== null && phase.previouslyBilledPercent !== undefined
            ? Number(phase.previouslyBilledPercent)
            : 0);

        const row = worksheet.addRow([
          phase.phaseName,
          Number(phase.contractAmount || 0),
          percentComplete / 100, // Divide by 100 for Excel percentage format
          { formula: `B${headerRow.number + 1 + index}*C${headerRow.number + 1 + index}` }
        ]);
        row.height = 20;

        ["A", "B", "C", "D"].forEach((col, colIndex) => {
          const cell = row.getCell(colIndex + 1);
          cell.font = { name: "Helvetica", size: 10 };
          cell.alignment = { vertical: "middle", horizontal: colIndex === 0 ? "left" : "center" };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" }
          };
          if (colIndex === 1) { // Amount
            cell.numFmt = '"$"#,##0.00';
          }
          if (colIndex === 2) { // Percent
            cell.numFmt = '0.00%';
          }
          if (colIndex === 3) { // Total
            cell.numFmt = '"$"#,##0.00';
          }
        });
      });
      const lastDataRow = headerRow.number + phases.length;

      // --- 6. Summaries ---
      const addSummaryRow = (label, value, isTotalDue = false) => {
        const row = worksheet.addRow(["", "", label, value]);

        // Label Cell (C)
        const labelCell = row.getCell(3);
        labelCell.font = { name: "Helvetica", size: 10, bold: true };
        labelCell.alignment = { horizontal: "right", vertical: "middle" };

        // Value Cell (D)
        const valueCell = row.getCell(4);
        valueCell.font = { name: "Helvetica", size: 10, bold: true };
        valueCell.alignment = { horizontal: "center", vertical: "middle" };
        valueCell.numFmt = '"$"#,##0.00';
        valueCell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" }
        };

        if (isTotalDue) {
          valueCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
        }
      };

      // Sub-Total row with formula
      const subTotalRow = worksheet.addRow(["", "", "Sub-Total:", { formula: `SUM(D${firstDataRow}:D${lastDataRow})` }]);
      const subTotalLabelCell = subTotalRow.getCell(3);
      subTotalLabelCell.font = { name: "Helvetica", size: 10, bold: true };
      subTotalLabelCell.alignment = { horizontal: "right", vertical: "middle" };
      const subTotalValueCell = subTotalRow.getCell(4);
      subTotalValueCell.font = { name: "Helvetica", size: 10, bold: true };
      subTotalValueCell.alignment = { horizontal: "center", vertical: "middle" };
      subTotalValueCell.numFmt = '"$"#,##0.00';
      subTotalValueCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
      };

      // Previously Invoiced - under Sub-Total (using formula to sum previously billed amounts)
      // Note: We need to calculate the sum of (contractAmount * previouslyBilledPercent / 100) for each phase
      // Since we don't have a column for previously billed amount in the table, we'll use a hardcoded value
      // but ideally this should be calculated from the phase data
      const prevInvoicedRow = worksheet.addRow(["", "", "Previously Invoiced:", Number(totalPrevAmount || 0)]);
      const prevInvoicedLabelCell = prevInvoicedRow.getCell(3);
      prevInvoicedLabelCell.font = { name: "Helvetica", size: 10, bold: true };
      prevInvoicedLabelCell.alignment = { horizontal: "right", vertical: "middle" };

      const prevInvoicedValueCell = prevInvoicedRow.getCell(4);
      prevInvoicedValueCell.font = { name: "Helvetica", size: 10, bold: true };
      prevInvoicedValueCell.alignment = { horizontal: "center", vertical: "middle" };
      prevInvoicedValueCell.numFmt = '"$"#,##0.00';
      prevInvoicedValueCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
      };

      addSummaryRow("Total Due:", { formula: `D${subTotalRow.number}-D${prevInvoicedRow.number}` }, true);

      // --- 7. Notes ---
      worksheet.addRow([]); // Blank line gap
      const notesHeaderRow = worksheet.addRow(["Projects Status Notes:", "", "", ""]);
      worksheet.mergeCells(`A${notesHeaderRow.number}:D${notesHeaderRow.number}`);
      const nhCell = notesHeaderRow.getCell(1);
      nhCell.font = { name: "Helvetica", size: 10, bold: true };
      nhCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF2F2F2" } };
      nhCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
      };

      // Notes Content
      const notesRow = worksheet.addRow([notes || "", "", "", ""]);
      worksheet.mergeCells(`A${notesRow.number}:D${notesRow.number + 2}`); // Span 3 rows
      const nCell = notesRow.getCell(1);
      nCell.font = { name: "Helvetica", size: 10 };
      nCell.alignment = { vertical: "top", wrapText: true };
      nCell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" }
      };

      // Advance rows manually since we merged

      // --- 8. Footer ---
      const f1 = worksheet.addRow(["Make all checks payable to Geopro Consultants, LLC"]);
      f1.getCell(1).font = { name: "Helvetica", size: 10 };

      const f2 = worksheet.addRow(["If you have any questions concerning this invoice, contact me."]);
      f2.getCell(1).font = { name: "Helvetica", size: 10 };


      const f4 = worksheet.addRow(["", "THANK YOU FOR YOUR BUSINESS!", "", ""]);
      worksheet.mergeCells(`A${f4.number}:D${f4.number}`);
      const tyCell = f4.getCell(1);
      tyCell.value = "THANK YOU FOR YOUR BUSINESS!";
      tyCell.font = { name: "Helvetica", size: 10, bold: true };
      tyCell.alignment = { horizontal: "center" };


      // Save
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const filename = invoiceNumber
        ? `Invoice_${invoiceNumber.replace(/\s+/g, "_")}.xlsx`
        : `Invoice_Draft_${new Date().toISOString().split("T")[0]}.xlsx`;
      saveAs(blob, filename);
      toast.success("Excel exported successfully");

    } catch (error) {
      console.error("Error exporting Excel:", error);
      toast.error("Failed to export Excel");
    }
  };

  if (isEditing && loadingInvoice) {
    return (
      <Box>
        <Typography variant="h4" fontWeight={600} gutterBottom mb={2}>
          Edit Invoice
        </Typography>
        <Paper sx={{ p: { xs: 2, md: 3 }, mx: { xs: -2, md: 0 }, borderRadius: { xs: 0, md: 1 } }}>
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        </Paper>
      </Box >
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight={600}>
          {isEditing ? "Edit Invoice" : "Add Invoice"}
        </Typography>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", "& > button": { flex: { xs: 1, sm: "initial" } } }}>
          {isEditing && !isEditable && (
            <Button variant="contained" color="secondary" onClick={() => setIsEditable(true)}>
              Edit Invoice
            </Button>
          )}
          <Button variant="contained" onClick={handleExportPDF} disabled={!phases.length}>
            Export PDF
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, mx: { xs: -2, md: 0 }, borderRadius: { xs: 0, md: 1 } }}>
        <Grid container rowSpacing={3} columnSpacing={{ xs: 0, md: 3 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Select Project *
            </Typography>
            <TextField
              select
              fullWidth
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              disabled={loadingProjects || isEditing || !isEditable}
              placeholder="Choose project"
            >
              <MenuItem value="" disabled>
                {loadingProjects ? "Loading projects..." : "Select a project"}
              </MenuItem>
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id.toString()}>
                  {project.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Invoice Date *
            </Typography>
            <TextField
              type="date"
              fullWidth
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              disabled={!isEditable}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Invoice Number *
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                fullWidth
                value={invoiceNumber}
                onChange={(e) => setInvoiceNumber(e.target.value)}
                placeholder="e.g., 2025-01"
                disabled={isEditing && !isEditable}
                error={!invoiceNumber && selectedProjectId}
                helperText={!invoiceNumber && selectedProjectId ? "Required" : ""}
              />
              {!isEditing && (
                <Button
                  variant="contained"
                  onClick={handleFindNextInvoiceNumber}
                  disabled={loadingInvoiceNumber}
                  sx={{ minWidth: "80px", height: "56px" }}
                >
                  {loadingInvoiceNumber ? "..." : "FIND"}
                </Button>
              )}
            </Box>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Status
            </Typography>
            <TextField
              select
              fullWidth
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              disabled={!isEditable}
            >
              <MenuItem value="SENT">SENT</MenuItem>
              <MenuItem value="PAID">PAID</MenuItem>
              <MenuItem value="LATE">LATE</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Status Date
            </Typography>
            <TextField
              type="date"
              fullWidth
              value={statusDate}
              onChange={(e) => setStatusDate(e.target.value)}
              disabled={!isEditable}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} md={12}>
            <Typography variant="subtitle1" fontWeight={500} gutterBottom>
              Invoice Notes
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!isEditable}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: { xs: 2, md: 3 }, mx: { xs: -2, md: 0 }, borderRadius: { xs: 0, md: 1 } }} ref={invoiceRef}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Invoice
        </Typography>
        <Grid container rowSpacing={3} columnSpacing={{ xs: 0, md: 3 }} sx={{ mb: 3 }}>
          {/* Left Column: Billed To */}
          <Grid item xs={12} md={6}>
            {projectSummary?.client && (
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Bill To:
                </Typography>
                <Typography variant="body2">
                  {projectSummary.client.company || projectSummary.client.clientNickname}
                </Typography>
                {projectSummary.client.contactPerson && (
                  <Typography variant="body2">
                    {projectSummary.client.contactPerson}
                  </Typography>
                )}
                {projectSummary.client.billingAddress && (
                  <Typography variant="body2">
                    {projectSummary.client.billingAddress}
                  </Typography>
                )}
                <Typography variant="body2">
                  {projectSummary.client.billingCity || projectSummary.client.city}, {projectSummary.client.billingState || projectSummary.client.state}{" "}
                  {projectSummary.client.billingZipCode || projectSummary.client.zipCode}
                </Typography>
                {projectSummary.client.accountsPayableEmail && (
                  <Typography variant="body2">
                    {projectSummary.client.accountsPayableEmail}
                  </Typography>
                )}
              </Box>
            )}
          </Grid>

          {/* Right Column: Project Tasks */}
          <Grid item xs={12} md={6}>
            {projectSummary?.invoiceDetails && projectSummary.invoiceDetails.trim() && (
              <Box>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Project Tasks
                </Typography>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "grey.50",
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "grey.300",
                  }}
                >
                  <Typography variant="body2" whiteSpace="pre-line">
                    {projectSummary.invoiceDetails}
                  </Typography>
                </Box>
              </Box>
            )}
          </Grid>
        </Grid>

        <Typography variant="h6" fontWeight={600} gutterBottom>
          Project Phases
        </Typography>
        {projectSummary && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              flexWrap: "wrap",
              mb: 2,
              gap: 2,
            }}
          >
            <Typography variant="body1">
              <strong>Project:</strong> {projectSummary.name || "N/A"}
            </Typography>
            <Typography variant="body1">
              <strong>Total Contract:</strong>{" "}
              {formatCurrency(totalContractAmount)}
            </Typography>
            <Typography variant="body1">
              <strong>Current Invoice Total:</strong>{" "}
              {formatCurrency(totalBillAmount)}
            </Typography>
            {projectSummary.client?.clientNickname && (
              <Typography variant="body1">
                <strong>Client:</strong>{" "}
                {projectSummary.client.clientNickname}
              </Typography>
            )}
          </Box>
        )}
        {loadingPhases ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : !selectedProjectId ? (
          <Typography variant="body2" color="text.secondary">
            Select a project to load its phases.
          </Typography>
        ) : phases.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            This project does not have any phases.
          </Typography>
        ) : (
          <TableContainer sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Phase</TableCell>
                  <TableCell align="right">Contract Amount</TableCell>
                  <TableCell align="right">Prev %</TableCell>
                  <TableCell align="right">Prev Amount</TableCell>
                  <TableCell align="center">New % Complete</TableCell>
                  <TableCell align="right">New Bill Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {phases.map((phase) => (
                  <TableRow key={phase.id}>
                    <TableCell>{phase.phaseName}</TableCell>
                    <TableCell align="right">
                      {formatCurrency(phase.contractAmount)}
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        value={phase.previouslyBilledPercent}
                        onChange={(e) =>
                          handlePrevPercentChange(phase.id, e.target.value)
                        }
                        inputProps={{ min: 0, max: 100 }}
                        disabled={!isEditable}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        value={phase.previouslyBilledAmount === "" ? "" : phase.previouslyBilledAmount}
                        onChange={(e) =>
                          handlePrevAmountChange(phase.id, e.target.value)
                        }
                        inputProps={{ min: 0 }}
                        disabled={!isEditable}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        size="small"
                        value={phase.newPercentComplete === "" ? "" : phase.newPercentComplete}
                        onChange={(e) =>
                          handlePercentChange(phase.id, e.target.value)
                        }
                        inputProps={{ min: phase.previouslyBilledPercent, max: 100 }}
                        error={Boolean(phase.error)}
                        helperText={phase.error}
                        disabled={!isEditable}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        size="small"
                        value={phase.newBillAmount === "" ? "" : phase.newBillAmount}
                        onChange={(e) =>
                          handleNewBillAmountChange(phase.id, e.target.value)
                        }
                        inputProps={{ min: 0 }}
                        disabled={!isEditable}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell
                    colSpan={3}
                    align="right"
                    sx={{ fontWeight: 600 }}
                  >
                    Totals
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatCurrency(totalPrevAmount)}
                  </TableCell>
                  <TableCell />
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {formatCurrency(totalBillAmount)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Project Tasks Summary Table */}
        {phases.length > 0 && phases.some((p) => Number(p.newBillAmount || 0) > 0) && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Project Tasks Summary
            </Typography>
            <TableContainer sx={{ mt: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Project Tasks</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Percent Complete</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {phases
                    .filter((phase) => Number(phase.newBillAmount || 0) > 0)
                    .map((phase) => (
                      <TableRow key={phase.id}>
                        <TableCell>{phase.phaseName}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(phase.contractAmount)}
                        </TableCell>
                        <TableCell align="right">
                          {phase.newPercentComplete ? `${phase.newPercentComplete}%` : "0%"}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(phase.newBillAmount || 0)}
                        </TableCell>
                      </TableRow>
                    ))}
                  <TableRow>
                    <TableCell colSpan={2} align="right" sx={{ fontWeight: 600 }}>
                      Sub-Total:
                    </TableCell>
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(totalBillAmount)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} align="right" sx={{ fontWeight: 600 }}>
                      Previously Invoiced:
                    </TableCell>
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(totalPrevAmount)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} align="right" sx={{ fontWeight: 700, fontSize: "1rem" }}>
                      Total Due:
                    </TableCell>
                    <TableCell />
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: "1rem" }}>
                      {formatCurrency(totalBillAmount)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}

        {notes && notes.trim() && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Invoice Notes
            </Typography>
            <Typography variant="body2" whiteSpace="pre-line">
              {notes}
            </Typography>
          </Box>
        )}
      </Paper>

      <Box sx={{ display: "flex", flexDirection: { xs: "column-reverse", sm: "row" }, justifyContent: "flex-end", gap: 2, mt: 3 }}>
        <Button variant="contained" onClick={() => navigate("/admin/invoices")}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleExportExcel}
          disabled={!phases.length}
        >
          Export Excel
        </Button>
        {isEditable && (
          <Button
            variant="contained"
            onClick={handleSaveInvoice}
            disabled={saving || (!isEditing && hasErrors) || !phases.length || loadingInvoice}
          >
            {saving ? (isEditing ? "Updating..." : "Saving...") : (isEditing ? "Update Invoice" : "Save Invoice")}
          </Button>
        )}
      </Box>
    </Box>
  );
}

export default AddInvoice;

